import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  TruckIcon, PackageOpenIcon, LogOutIcon, FileSpreadsheetIcon, CheckCircle2Icon,
  Trash2Icon, CalendarClockIcon, ShieldCheckIcon, ScaleIcon, UsersIcon, PlusIcon,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import * as XLSX from "xlsx";
import { Switch } from "@/components/ui/switch";

/* ================= PORTAL DO FORNECEDOR — ÁREA ISOLADA ================= */

const TIPOS_OPERACAO = [
  { v: "RECEBIMENTO", label: "Recebimento (Entrega de Carga)" },
  { v: "COLETA", label: "Coleta" },
  { v: "DEVOLUCAO", label: "Devolução" },
];

const STATUS_CORES = {
  AGENDADO: "bg-[#00E5FF]/10 text-[#00E5FF] border-[#00E5FF]/40",
  CHEGOU: "bg-[#FFD60A]/10 text-[#FFD60A] border-[#FFD60A]/40",
  EM_CONFERENCIA: "bg-[#FF9F0A]/10 text-[#FF9F0A] border-[#FF9F0A]/40",
  FINALIZADO: "bg-[#34C759]/10 text-[#34C759] border-[#34C759]/40",
};

const fmtDT = (s) => (s ? new Date(s).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
const inputCls = "mt-1 rounded-none bg-[#0A0A0C] border-border h-11";
const HORAS = Array.from({ length: 13 }, (_, i) => `${String(6 + i).padStart(2, "0")}:00`);

export default function FornecedorPortal() {
  const { user, logout } = useAuth();
  const [perfil, setPerfil] = useState(null);
  const [aba, setAba] = useState("agendamento");
  const [docas, setDocas] = useState([]);
  const [agendamentos, setAgendamentos] = useState([]);
  const [devolucoes, setDevolucoes] = useState([]);
  const [operadores, setOperadores] = useState([]);
  const [opForm, setOpForm] = useState({ nome: "", cpf: "", telefone: "" });
  const fileRef = useRef(null);
  const [form, setForm] = useState({ doca_codigo: "", data: "", hora: "08:00", motorista: "", cpf: "", placa: "", tipo_operacao: "RECEBIMENTO", operador_id: "" });
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [enviando, setEnviando] = useState(false);

  const load = async () => {
    try {
      const [me, d, a, ops] = await Promise.all([
        api.get("/fornecedor/me"), api.get("/fornecedor/docas"), api.get("/fornecedor/agendamentos"),
        api.get("/fornecedor/operadores").catch(() => ({ data: [] })),
      ]);
      setPerfil(me.data);
      setDocas(d.data);
      setAgendamentos(a.data);
      setOperadores(ops.data);
      if (me.data?.recebimento_vazios_enabled) {
        const dev = await api.get("/fornecedor/devolucoes").catch(() => ({ data: [] }));
        setDevolucoes(dev.data);
      }
    } catch { /* sessão inválida tratada pelo interceptor */ }
  };
  useEffect(() => { load(); }, []);

  const lerPlanilha = async (file) => {
    if (!file) return;
    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) return toast.error("Use .xlsx, .xls ou .csv");
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const parsed = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });
      if (!parsed.length) return toast.error("Planilha vazia");
      setRows(parsed);
      setFileName(file.name);
      toast.success(`${parsed.length} linhas lidas da planilha`);
    } catch (e) { toast.error(`Erro lendo planilha: ${e.message}`); }
  };

  const agendar = async () => {
    if (!form.doca_codigo) return toast.error("Escolha a Doca");
    if (!form.data) return toast.error("Escolha a Data");
    if (!form.motorista.trim()) return toast.error("Informe o Nome do Motorista");
    if (!form.cpf.trim()) return toast.error("Informe o CPF do Motorista");
    if (!form.placa.trim()) return toast.error("Informe a Placa do Veículo");
    if (!rows.length) return toast.error("Anexe a planilha Excel de recebimento");
    setEnviando(true);
    try {
      const payload = { ...form, operador_id: form.operador_id || null, itens: rows };
      const { data } = await api.post("/fornecedor/agendamentos", payload);
      toast.success(`Agendamento ${data.numero} criado — ${data.total_itens} contentor(es)`);
      setForm({ doca_codigo: "", data: "", hora: "08:00", motorista: "", cpf: "", placa: "", tipo_operacao: "RECEBIMENTO", operador_id: "" });
      setRows([]); setFileName("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setEnviando(false); }
  };

  const salvarOperador = async () => {
    if (!opForm.nome.trim()) return toast.error("Informe o nome do operador");
    try {
      await api.post("/fornecedor/operadores", opForm);
      toast.success(`Operador ${opForm.nome} cadastrado`);
      setOpForm({ nome: "", cpf: "", telefone: "" });
      const ops = await api.get("/fornecedor/operadores");
      setOperadores(ops.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleOperador = async (op) => {
    try {
      await api.patch(`/fornecedor/operadores/${op.id}`, { ativo: !op.ativo });
      const ops = await api.get("/fornecedor/operadores");
      setOperadores(ops.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const excluirOperador = async (op) => {
    if (!window.confirm(`Excluir operador ${op.nome}?`)) return;
    try {
      await api.delete(`/fornecedor/operadores/${op.id}`);
      const ops = await api.get("/fornecedor/operadores");
      setOperadores(ops.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const previewCols = useMemo(() => rows.length ? Object.keys(rows[0]).slice(0, 6) : [], [rows]);

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white" data-testid="fornecedor-portal">
      {/* Header isolado */}
      <header className="border-b border-border bg-[#14151A] px-6 py-4 flex items-center gap-4 flex-wrap">
        <div className="w-10 h-10 bg-[#FFD60A] flex items-center justify-center">
          <ShieldCheckIcon className="w-6 h-6 text-black" strokeWidth={2.5} />
        </div>
        <div>
          <div className="font-display text-xl font-black leading-none">PORTAL DO <span className="text-[#FFD60A]">FORNECEDOR</span></div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF]">CHEM·WMS · Base {perfil?.base_nome || "—"}</div>
        </div>
        <div className="ml-auto flex items-center gap-4">
          <div className="text-right">
            <div className="text-sm font-bold" data-testid="fornecedor-nome">{user?.name}</div>
            <div className="text-[10px] text-muted-foreground font-mono">@{user?.email}</div>
          </div>
          <Button variant="outline" onClick={logout} data-testid="fornecedor-logout" className="rounded-none border-border hover:border-[#FF3B30] hover:text-[#FF3B30] h-9 text-xs uppercase font-bold">
            <LogOutIcon className="w-3.5 h-3.5 mr-1" /> Sair
          </Button>
        </div>
      </header>

      {/* Abas restritas */}
      <div className="px-6 pt-5">
        <div className="flex border border-border w-fit">
          <button onClick={() => setAba("agendamento")} data-testid="forn-tab-agendamento"
                  className={`px-5 h-11 text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${aba === "agendamento" ? "bg-[#FFD60A] text-black" : "bg-[#14151A] text-muted-foreground hover:text-white"}`}>
            <TruckIcon className="w-4 h-4" /> Entregar Carga Cheia
          </button>
          <button onClick={() => setAba("operadores")} data-testid="forn-tab-operadores"
                  className={`px-5 h-11 text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${aba === "operadores" ? "bg-[#00E5FF] text-black" : "bg-[#14151A] text-muted-foreground hover:text-white"}`}>
            <UsersIcon className="w-4 h-4" /> Meus Operadores ({operadores.length})
          </button>
          {perfil?.recebimento_vazios_enabled && (
            <button onClick={() => setAba("vazios")} data-testid="forn-tab-vazios"
                    className={`px-5 h-11 text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${aba === "vazios" ? "bg-[#34C759] text-black" : "bg-[#14151A] text-muted-foreground hover:text-white"}`}>
              <PackageOpenIcon className="w-4 h-4" /> Recebimento de Vazios
            </button>
          )}
        </div>
      </div>

      <main className="p-6 space-y-6">
        {aba === "agendamento" && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Novo agendamento */}
            <Card className="bg-[#14151A] border-border rounded-none p-5 space-y-4" data-testid="forn-form-agendamento">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#FFD60A] flex items-center gap-2">
                <CalendarClockIcon className="w-4 h-4" /> Novo Agendamento de Entrega
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Tipo de Operação</Label>
                  <Select value={form.tipo_operacao} onValueChange={(v) => setForm({ ...form, tipo_operacao: v })}>
                    <SelectTrigger data-testid="forn-tipo-operacao" className="mt-1 rounded-none bg-[#0A0A0C] h-11 border-border"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#14151A] border-border rounded-none">
                      {TIPOS_OPERACAO.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Operador Responsável (opcional)</Label>
                  <Select value={form.operador_id} onValueChange={(v) => setForm({ ...form, operador_id: v === "_none" ? "" : v })}>
                    <SelectTrigger data-testid="forn-operador" className="mt-1 rounded-none bg-[#0A0A0C] h-11 border-border"><SelectValue placeholder={operadores.length ? "Quem está agendando?" : "Cadastre operadores na aba Meus Operadores"} /></SelectTrigger>
                    <SelectContent className="bg-[#14151A] border-border rounded-none">
                      <SelectItem value="_none">— Sem operador —</SelectItem>
                      {operadores.filter((o) => o.ativo).map((o) => <SelectItem key={o.id} value={o.id}>{o.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Doca Disponível</Label>
                  <Select value={form.doca_codigo} onValueChange={(v) => setForm({ ...form, doca_codigo: v })}>
                    <SelectTrigger data-testid="forn-doca" className="mt-1 rounded-none bg-[#0A0A0C] h-11 border-border"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent className="bg-[#14151A] border-border rounded-none">
                      {docas.map((d) => <SelectItem key={d.id} value={d.codigo}>{d.codigo}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Data</Label>
                  <Input type="date" data-testid="forn-data" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} className={`${inputCls} font-mono`} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Horário</Label>
                  <Select value={form.hora} onValueChange={(v) => setForm({ ...form, hora: v })}>
                    <SelectTrigger data-testid="forn-hora" className="mt-1 rounded-none bg-[#0A0A0C] h-11 border-border font-mono"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#14151A] border-border rounded-none">
                      {HORAS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Nome do Motorista</Label>
                  <Input data-testid="forn-motorista" value={form.motorista} onChange={(e) => setForm({ ...form, motorista: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">CPF</Label>
                  <Input data-testid="forn-cpf" value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} className={`${inputCls} font-mono`} placeholder="000.000.000-00" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Placa do Veículo</Label>
                  <Input data-testid="forn-placa" value={form.placa} onChange={(e) => setForm({ ...form, placa: e.target.value.toUpperCase() })} className={`${inputCls} font-mono`} placeholder="ABC1D23" />
                </div>
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#00E5FF]">Planilha Excel de Recebimento</Label>
                <div className="text-[10px] text-muted-foreground mb-2">Colunas: Pedido, Item, Contentor, Produto, Qtd, Lacres, Tara, Lote, Data de Validade do Lote, NF Produto, Conta e Ordem, NF Contentor</div>
                {!rows.length ? (
                  <label className="border border-dashed border-[#00E5FF]/50 hover:bg-[#00E5FF]/5 h-20 flex items-center justify-center gap-3 cursor-pointer text-sm text-[#00E5FF]">
                    <FileSpreadsheetIcon className="w-5 h-5" /> Clique para anexar a planilha (.xlsx/.xls/.csv)
                    <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={(e) => lerPlanilha(e.target.files[0])} className="hidden" data-testid="forn-planilha" />
                  </label>
                ) : (
                  <div className="border border-[#34C759]/40 bg-[#34C759]/5 p-3" data-testid="forn-planilha-preview">
                    <div className="flex items-center gap-3">
                      <FileSpreadsheetIcon className="w-5 h-5 text-[#34C759]" />
                      <div className="flex-1">
                        <div className="text-sm font-bold">{fileName}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">{rows.length} contentor(es)/linha(s) lidas</div>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => { setRows([]); setFileName(""); if (fileRef.current) fileRef.current.value = ""; }} className="text-[#FF3B30] h-8">
                        <Trash2Icon className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="mt-2 overflow-x-auto">
                      <table className="text-[10px] font-mono w-full">
                        <thead><tr>{previewCols.map((c) => <th key={c} className="text-left pr-4 text-muted-foreground uppercase">{c}</th>)}</tr></thead>
                        <tbody>
                          {rows.slice(0, 3).map((r, i) => (
                            <tr key={i}>{previewCols.map((c) => <td key={c} className="pr-4 text-white/80">{String(r[c] || "—")}</td>)}</tr>
                          ))}
                        </tbody>
                      </table>
                      {rows.length > 3 && <div className="text-[9px] text-muted-foreground mt-1">+ {rows.length - 3} linhas...</div>}
                    </div>
                  </div>
                )}
              </div>

              <Button onClick={agendar} disabled={enviando} data-testid="forn-agendar" className="rounded-none bg-[#FFD60A] text-black hover:bg-[#ffdf3a] font-bold uppercase tracking-wider w-full h-12">
                <CheckCircle2Icon className="w-4 h-4 mr-2" /> {enviando ? "Enviando..." : "Confirmar Agendamento"}
              </Button>
            </Card>

            {/* Meus agendamentos */}
            <Card className="bg-[#14151A] border-border rounded-none overflow-hidden" data-testid="forn-lista-agendamentos">
              <div className="p-4 border-b border-border text-[11px] uppercase tracking-[0.3em] text-[#00E5FF]">Meus Agendamentos</div>
              <div className="divide-y divide-border/50 max-h-[560px] overflow-y-auto">
                {agendamentos.length === 0 && <div className="p-8 text-center text-xs text-muted-foreground">Nenhum agendamento ainda.</div>}
                {agendamentos.map((a) => (
                  <div key={a.id} className="p-4 flex items-start gap-4 flex-wrap" data-testid={`forn-ag-${a.numero}`}>
                    <div className="font-mono text-[#FFD60A] font-bold">{a.numero}</div>
                    <div className="flex-1 min-w-[180px]">
                      <div className="text-sm font-bold">{a.data_planejada?.split("-").reverse().join("/")} às {a.hora_planejada} · Doca {a.doca_codigo}</div>
                      <div className="text-[11px] text-muted-foreground">{a.motorista} · {a.placa} · {a.total_itens} contentor(es)</div>
                      {a.peso_liquido != null && (
                        <div className="text-[11px] font-mono text-[#34C759] flex items-center gap-1 mt-1"><ScaleIcon className="w-3 h-3" /> Peso líquido recebido: {a.peso_liquido.toLocaleString("pt-BR")} kg</div>
                      )}
                    </div>
                    <Badge className={`rounded-none border text-[10px] font-bold ${STATUS_CORES[a.status] || "bg-muted/20 text-muted-foreground border-border"}`}>{a.status.replace("_", " ")}</Badge>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {aba === "operadores" && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6" data-testid="forn-operadores">
            <Card className="bg-[#14151A] border-border rounded-none p-5 space-y-4">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#00E5FF] flex items-center gap-2">
                <UsersIcon className="w-4 h-4" /> Cadastrar Operador (Representante)
              </div>
              <div className="text-[10px] text-muted-foreground">Operadores são as pessoas da sua empresa autorizadas a fazer agendamentos neste portal.</div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Nome Completo</Label>
                <Input data-testid="op-nome" value={opForm.nome} onChange={(e) => setOpForm({ ...opForm, nome: e.target.value })} className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">CPF (opcional)</Label>
                  <Input data-testid="op-cpf" value={opForm.cpf} onChange={(e) => setOpForm({ ...opForm, cpf: e.target.value })} className={`${inputCls} font-mono`} placeholder="000.000.000-00" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Telefone (opcional)</Label>
                  <Input data-testid="op-telefone" value={opForm.telefone} onChange={(e) => setOpForm({ ...opForm, telefone: e.target.value })} className={`${inputCls} font-mono`} placeholder="(21) 99999-0000" />
                </div>
              </div>
              <Button onClick={salvarOperador} data-testid="op-salvar" className="rounded-none bg-[#00E5FF] text-black hover:bg-[#00E5FF]/85 font-bold uppercase tracking-wider w-full h-11">
                <PlusIcon className="w-4 h-4 mr-2" /> Cadastrar Operador
              </Button>
            </Card>

            <Card className="bg-[#14151A] border-border rounded-none overflow-hidden">
              <div className="p-4 border-b border-border text-[11px] uppercase tracking-[0.3em] text-[#FFD60A]">Operadores Cadastrados ({operadores.length})</div>
              <div className="divide-y divide-border/50 max-h-[480px] overflow-y-auto">
                {operadores.length === 0 && <div className="p-8 text-center text-xs text-muted-foreground">Nenhum operador cadastrado ainda.</div>}
                {operadores.map((op) => (
                  <div key={op.id} className="p-3 flex items-center gap-3" data-testid={`op-row-${op.id}`}>
                    <div className="flex-1">
                      <div className="text-sm font-bold">{op.nome} {!op.ativo && <Badge className="rounded-none bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/40 text-[9px] ml-1">INATIVO</Badge>}</div>
                      <div className="text-[10px] font-mono text-muted-foreground">{op.cpf || "—"} · {op.telefone || "—"}</div>
                    </div>
                    <Switch checked={!!op.ativo} onCheckedChange={() => toggleOperador(op)} data-testid={`op-switch-${op.id}`} />
                    <Button size="sm" variant="ghost" onClick={() => excluirOperador(op)} data-testid={`op-del-${op.id}`} className="h-7 w-7 p-0 text-muted-foreground hover:text-[#FF3B30]">
                      <Trash2Icon className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {aba === "vazios" && perfil?.recebimento_vazios_enabled && (
          <Card className="bg-[#14151A] border-border rounded-none overflow-hidden" data-testid="forn-vazios">
            <div className="p-4 border-b border-border text-[11px] uppercase tracking-[0.3em] text-[#34C759]">Embalagens Vazias Devolvidas pela Base</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                  <th className="px-4 py-2">Nº</th>
                  <th className="px-4 py-2">Data</th>
                  <th className="px-4 py-2">Motorista / Placa</th>
                  <th className="px-4 py-2">Contentores Devolvidos</th>
                  <th className="px-4 py-2">Qtd</th>
                  <th className="px-4 py-2">Peso Saída</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {devolucoes.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground text-xs">Nenhuma devolução registrada ainda.</td></tr>
                )}
                {devolucoes.map((d) => (
                  <tr key={d.id} className="border-b border-border/50" data-testid={`forn-dev-${d.numero}`}>
                    <td className="px-4 py-2 font-mono text-[#FFD60A]">{d.numero}</td>
                    <td className="px-4 py-2 font-mono text-xs">{fmtDT(d.hora_checkout || d.created_at)}</td>
                    <td className="px-4 py-2 text-xs">{d.motorista} · <span className="font-mono">{d.placa}</span></td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(d.contentores || []).map((c) => (
                          <span key={c} className="font-mono text-[10px] bg-[#34C759]/10 text-[#34C759] border border-[#34C759]/30 px-1.5 py-0.5">{c}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-2 font-mono text-center">{(d.contentores || []).length}</td>
                    <td className="px-4 py-2 font-mono text-xs">{d.peso_saida ? `${d.peso_saida.toLocaleString("pt-BR")} kg` : "—"}</td>
                    <td className="px-4 py-2">
                      <Badge className={`rounded-none border text-[10px] font-bold ${d.status === "FINALIZADO" ? STATUS_CORES.FINALIZADO : STATUS_CORES.EM_CONFERENCIA}`}>{d.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </main>
    </div>
  );
}
